// Fallback global style for generated disabled input class (for any missed edge cases)
// Use injectColorDisabledInputGlobalStyle() in your App.tsx to apply this fix for disabled input color.
import { createTheme } from '@mui/material/styles';

export function injectColorDisabledInputGlobalStyle() {
  if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.innerHTML = `
      .MuiInputBase-input.Mui-disabled,
      .MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled,
      .css-hi8heo-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled {
        opacity: 1 !important;
        -webkit-text-fill-color: rgba(0,0,0,.87) !important;
        color: rgba(0,0,0,.87) !important;
      }
    `;
    document.head.appendChild(style);
  }
}

export const COLORS = {
  PRIMARY_BLUE: '#36bde8', // Main brand blue used for buttons, links, borders
  WHITE: '#fff', // Primary text color on blue backgrounds
  BORDER_FOCUS: '#36bde8', // Input field focus border color
  TEXT_SECONDARY: 'rgba(0, 0, 0, 0.54)', // Secondary text color
  ERROR_TEXT: 'rgba(242, 27, 27, 0.83)', // Error text color
  ERROR_BORDER: '#f21b1bd4', // Error border color
  TRANSPARENT: 'transparent',
  HIGHLIGHT_YELLOW: '#ff0', // Selection highlight
  HIGHLIGHT_TEXT: '#000', // Text on yellow highlight
  CODE_BACKGROUND: 'hsla(0, 0%, 0%, 0.04)', // Code block background
  ACCOUNT_HOVER: '#837E7C', // Account menu hover background
  ACCOUNT_DIVIDER: 'gray', // Account menu divider color
  ACCOUNT_BUTTON_BG: '#4F4F4F', // Account menu button background
  ACCOUNT_BUTTON_HOVER: '#837E7C', // Account menu button hover background
  TEXT_PRIMARY: 'rgba(0, 0, 0, 0.87)',
  TEXT_MUTED: 'hsla(0, 0%, 0.8)',
} as const;

// Spacing Constants

export const SPACING = {
  FORM_PADDING: '1rem',
  CARD_HEADER_PADDING: 8,
  CONTAINER_PADDING: 16,
  CONTENT_PADDING: 24,
  LAYOUT_PADDING: '0 16px',
  BUTTON_PADDING_LEFT: '20px',
  DIALOG_PADDING: '16px',
  LOGO_MARGIN_TOP: 4,
} as const;

// Icon Styles (must be after COLORS and all theme constants)
export const ICON_STYLES = {
  error: {
    color: COLORS.ERROR_TEXT,
    margin: '10px 10px',
    marginLeft: '-3px',
    height: 'auto',
  },
};

// Component Size Constants
export const SIZES = {
  SIDEBAR_WIDTH: 260,
  MIN_BUTTON_WIDTH: '80px',
  PROGRESS_ICON_SIZE: '30px',
} as const;

// Z-Index Constants
export const Z_INDEX = {
  PROGRESS_OVERLAY: 1000,
  DIALOG: 1300,
  TOOLTIP: 1500,
} as const;

// Typography Constants
export const TYPOGRAPHY = {
  DIALOG_FONT_SIZE: '15px',
  ACCOUNT_USERNAME_SIZE: '12px',
  ACCOUNT_BUTTON_SIZE: '13px',
  ACCOUNT_USERNAME_FONT: 'Calibri Light',
  ACCOUNT_EXTENSION_FONT: 'system-ui',
  ACCOUNT_BUTTON_FONT: 'sans-serif',
} as const;

// MUI v5 Theme

const theme = createTheme({
  palette: {
    primary: {
      main: COLORS.PRIMARY_BLUE,
    },
    secondary: {
      main: COLORS.PRIMARY_BLUE,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
      primary: COLORS.TEXT_PRIMARY,
      secondary: COLORS.TEXT_SECONDARY,
    },
    error: {
      main: '#d32f2f',
    },
    background: {
      default: COLORS.WHITE,
      paper: COLORS.WHITE,
    },
  },
  typography: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
    h6: {
      fontSize: '1.25rem',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontWeight: 500,
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '.MuiProgressOverlay': {
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.5)',
          zIndex: 2000,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#0000008a !important',
          '&.Mui-focused': {
            color: '#0000008a !important',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            color: COLORS.TEXT_SECONDARY,
          },

          '&.showInputErrorBorder.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.ERROR_BORDER,
            borderWidth: '2px',
          },
          '&.showInputErrorBorder.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              borderColor: COLORS.ERROR_BORDER,
              borderWidth: '1px',
            },
        },
        notchedOutline: {
          borderWidth: '1px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: COLORS.TEXT_PRIMARY,
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #fff inset',
            boxShadow: '0 0 0 1000px #fff inset',
            backgroundColor: '#fff !important',
            color: 'inherit',
            caretColor: 'inherit',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: COLORS.ERROR_BORDER,
          backgroundColor: COLORS.WHITE,
          border: '1px solid #ddd',
          boxShadow: '-1px 4px 5px 0px #969696',
          padding: '10px',
          fontSize: '0.8rem',
          bottom: '10px',
        },
        arrow: {
          fontSize: '25px',
          color: COLORS.WHITE,
          '&::before': {
            background: COLORS.WHITE,
            border: '1px solid #ddd',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          color: COLORS.PRIMARY_BLUE,
        },
        colorSecondary: {
          color: '#f50057',
        },
        root: {
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingBottom: '10px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: '0 0 0 0',
        },
      },
    },
  },
});

export default theme;
